/*
  Page d'accueil Maison Kimono : comportements des composants mk-*.
  Aucun contenu n'est créé ici (hors copies de slides pour la boucle infinie) :
  tout le texte et tous les liens sont dans le HTML rendu par Liquid.
*/

const mouvementReduit = window.matchMedia('(prefers-reduced-motion: reduce)');
const ecranLarge = window.matchMedia('(min-width: 750px)');

/** Courbe cubic-bezier(.45, 0, .2, 1) de la maquette. */
function courbe(t) {
  const x1 = 0.45;
  const y1 = 0;
  const x2 = 0.2;
  const y2 = 1;
  const bezier = (u, a, b) => 3 * (1 - u) * (1 - u) * u * a + 3 * (1 - u) * u * u * b + u * u * u;
  let debut = 0;
  let fin = 1;
  let u = t;
  for (let i = 0; i < 14; i++) {
    u = (debut + fin) / 2;
    if (bezier(u, x1, x2) < t) debut = u;
    else fin = u;
  }
  return bezier(u, y1, y2);
}

/**
 * Minuterie de défilement automatique, arrêtée hors écran, onglet caché,
 * au survol, au focus clavier et pendant un geste.
 */
class Rotation {
  constructor(element, intervalle, action) {
    this.element = element;
    this.intervalle = intervalle;
    this.action = action;
    this.visible = false;
    this.pauses = new Set();
    this.minuterie = 0;

    this.observateur = new IntersectionObserver((entrees) => {
      this.visible = entrees[entrees.length - 1].isIntersecting;
      this.regler();
    });
    this.observateur.observe(element);

    this.regler = this.regler.bind(this);
    document.addEventListener('visibilitychange', this.regler);
    mouvementReduit.addEventListener('change', this.regler);

    element.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'mouse') this.pause('survol');
    });
    element.addEventListener('pointerleave', (event) => {
      if (event.pointerType === 'mouse') this.reprise('survol');
    });
    element.addEventListener('focusin', (event) => {
      if (event.target instanceof Element && event.target.matches(':focus-visible')) this.pause('focus');
    });
    element.addEventListener('focusout', () => this.reprise('focus'));
  }

  pause(raison) {
    this.pauses.add(raison);
    this.regler();
  }

  reprise(raison) {
    this.pauses.delete(raison);
    this.regler();
  }

  regler() {
    const actif = this.visible && !document.hidden && this.pauses.size === 0 && !mouvementReduit.matches;
    if (actif && !this.minuterie) {
      this.minuterie = window.setInterval(this.action, this.intervalle);
    } else if (!actif && this.minuterie) {
      window.clearInterval(this.minuterie);
      this.minuterie = 0;
    }
  }

  /** Repart pour un intervalle complet (après un geste ou un clic sur une flèche). */
  relancer() {
    if (!this.minuterie) return;
    window.clearInterval(this.minuterie);
    this.minuterie = 0;
    this.regler();
  }

  arreter() {
    this.observateur.disconnect();
    document.removeEventListener('visibilitychange', this.regler);
    mouvementReduit.removeEventListener('change', this.regler);
    window.clearInterval(this.minuterie);
    this.minuterie = 0;
  }
}

/* -------------------------------------------------------------------------- */
/* Les coups de coeur                                                         */
/* -------------------------------------------------------------------------- */

class MkCoupsDeCoeur extends HTMLElement {
  connectedCallback() {
    if (this.pret) return;
    this.scene = this.querySelector('.mk-cdc__scene');
    this.piste = this.querySelector('.mk-cdc__piste');
    if (!this.scene || !this.piste) return;
    this.originaux = Array.from(this.piste.children);
    if (!this.originaux.length) return;
    this.pret = true;

    /* Rien au chargement de la page : le carrousel est sous la ligne de flottaison
       et le rendu Liquid suffit. On ne duplique les slides et on ne calcule qu'à
       l'approche de l'écran, pour ne pas retarder l'affichage du haut de page. */
    this.observateurDepart = new IntersectionObserver(
      (entrees) => {
        if (entrees[entrees.length - 1].isIntersecting) this.demarrer();
      },
      { rootMargin: '100px 0px' }
    );
    this.observateurDepart.observe(this.scene);
    /* Filet de sécurité si l'observateur ne se déclenche jamais (onglet en arrière-plan) :
       on prépare la boucle, mais les photos attendent quand même l'approche de l'écran. */
    this.minuterieDepart = setTimeout(() => this.demarrer(false), 5000);
  }

  /* MK 22/10/2026 : les photos n'ont pas d'adresse (data-src) tant que le carrousel n'approche
     pas de l'écran (100 px au lieu de 600) : avec loading="lazy" seul, les 8 photos (~500 Ko)
     partaient dès l'ouverture de la page. On rétablit les adresses ici, avant la duplication
     des slides, pour que les copies les aient aussi. */
  chargerPhotos() {
    for (const img of this.piste.querySelectorAll('img[data-src]')) {
      if (img.dataset.srcset) img.setAttribute('srcset', img.dataset.srcset);
      img.setAttribute('src', img.dataset.src);
      img.removeAttribute('data-src');
      img.removeAttribute('data-srcset');
    }
  }

  demarrer(avecPhotos = true) {
    if (this.demarre) return;
    this.demarre = true;
    clearTimeout(this.minuterieDepart);
    this.observateurDepart.disconnect();
    if (avecPhotos) {
      this.chargerPhotos();
    } else {
      this.observateurPhotos = new IntersectionObserver(
        (entrees) => {
          if (!entrees[entrees.length - 1].isIntersecting) return;
          this.observateurPhotos.disconnect();
          this.chargerPhotos();
        },
        { rootMargin: '100px 0px' }
      );
      this.observateurPhotos.observe(this.scene);
    }

    this.points = Array.from(this.querySelectorAll('.mk-cdc__point'));
    this.slides = [];
    this.enPause = false;
    this.visible = true;

    this.surDefilement = this.surDefilement.bind(this);
    this.scene.addEventListener('scroll', this.surDefilement, { passive: true });

    /* On observe la largeur du carrousel, pas la fenêtre : sur téléphone, la barre
       d'adresse qui se replie déclenche un resize sans changer la largeur. */
    let attente = 0;
    this.observateurTaille = new ResizeObserver(() => {
      clearTimeout(attente);
      attente = setTimeout(() => this.installer(), 150);
    });
    this.observateurTaille.observe(this.scene);

    /* Le survol ne met en pause qu'avec une vraie souris, et sur les photos seulement. */
    this.scene.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'mouse') this.pause();
    });
    this.scene.addEventListener('pointerleave', (event) => {
      if (event.pointerType === 'mouse') this.reprise();
    });
    this.scene.addEventListener('touchstart', () => this.pause(), { passive: true });
    this.scene.addEventListener('touchend', () => this.repriseDifferee(), { passive: true });
    this.scene.addEventListener('touchcancel', () => this.repriseDifferee(), { passive: true });
    this.addEventListener('focusin', (event) => {
      if (event.target instanceof Element && event.target.matches(':focus-visible')) this.pause();
    });
    this.addEventListener('focusout', () => this.reprise());

    this.brancherSouris();

    for (const fleche of this.querySelectorAll('.mk-cdc__fleche')) {
      fleche.addEventListener('click', () => {
        this.avancer(Number(fleche.dataset.sens));
        this.lancer();
      });
    }

    /* Ne consomme rien tant que le carrousel n'est pas à l'écran. */
    this.observateur = new IntersectionObserver(
      (entrees) => {
        this.visible = entrees[entrees.length - 1].isIntersecting;
        this.visible ? this.lancer() : this.arreter();
      },
      { rootMargin: '100px' }
    );
    this.observateur.observe(this.scene);

    this.surVisibilite = () => (document.hidden ? this.arreter() : this.lancer());
    document.addEventListener('visibilitychange', this.surVisibilite);

    this.installer();
  }

  disconnectedCallback() {
    this.arreter();
    this.observateurDepart?.disconnect();
    this.observateurPhotos?.disconnect();
    clearTimeout(this.minuterieDepart);
    this.demarre = false;
    clearTimeout(this.minuterieReprise);
    clearTimeout(this.minuterieCalage);
    cancelAnimationFrame(this.image);
    this.observateur?.disconnect();
    this.observateurTaille?.disconnect();
    this.scene?.removeEventListener('scroll', this.surDefilement);
    if (this.surVisibilite) document.removeEventListener('visibilitychange', this.surVisibilite);
    this.pret = false;
  }

  /* ---- Dimensions et boucle ---- */

  /** Lit le pas dans le CSS (il change entre mobile et PC), duplique, recentre. */
  installer() {
    const largeur = this.scene.clientWidth;
    const style = getComputedStyle(this);
    const pas = parseFloat(style.getPropertyValue('--sw')) + parseFloat(style.getPropertyValue('--g'));
    if (!largeur || !pas || (largeur === this.largeur && pas === this.pas)) return;
    this.largeur = largeur;
    this.pas = pas;
    this.style.setProperty('--bord', `${(largeur - parseFloat(style.getPropertyValue('--sw'))) / 2}px`);
    this.jeu = this.originaux.length * pas;

    this.completer(largeur);
    this.recentrer(true);
    this.afficher(true);
    this.lancer();
  }

  /** Duplique le jeu d'origine : de quoi couvrir l'écran plus un jeu de chaque côté. */
  completer(largeur) {
    const besoin = Math.max(3, Math.ceil(largeur / this.jeu) + 2);
    if (this.piste.children.length / this.originaux.length === besoin) return;

    const fragment = document.createDocumentFragment();
    fragment.append(...this.originaux);
    for (let i = 1; i < besoin; i++) {
      for (const original of this.originaux) {
        const copie = original.cloneNode(true);
        copie.setAttribute('aria-hidden', 'true');
        copie.removeAttribute('data-shopify-editor-block');
        for (const element of copie.querySelectorAll('a, button')) element.setAttribute('tabindex', '-1');
        for (const image of copie.querySelectorAll('img')) image.setAttribute('alt', '');
        fragment.appendChild(copie);
      }
    }
    this.piste.replaceChildren(fragment);
    this.slides = Array.from(this.piste.children);
  }

  /** Ramène le défilement dans le jeu du milieu, sans transition visible. */
  recentrer(force = false) {
    if (!this.jeu || this.anime || this.glisse) return;
    if (force) {
      this.scene.scrollLeft = this.jeu;
      this.afficher(true);
      return;
    }
    const gauche = this.scene.scrollLeft;
    if (gauche < this.jeu * 0.5) this.scene.scrollLeft = gauche + this.jeu;
    else if (gauche > this.jeu * 1.5) this.scene.scrollLeft = gauche - this.jeu;
    else return;
    this.afficher();
  }

  surDefilement() {
    if (!this.attenteImage) {
      this.attenteImage = true;
      requestAnimationFrame(() => {
        this.attenteImage = false;
        this.afficher();
      });
    }
    if (this.anime || this.glisse) return;
    /* Recentrage une fois le défilement retombé, pour ne pas couper un geste. */
    clearTimeout(this.minuterieCalage);
    this.minuterieCalage = setTimeout(() => this.recentrer(), 150);
  }

  /**
   * Taille et décalage de chaque slide d'après la position du défilement :
   * la slide i est centrée exactement quand scrollLeft vaut i × pas.
   */
  afficher(force = false) {
    if (!this.pas) return;
    const position = this.scene.scrollLeft / this.pas;
    const total = this.slides.length;
    for (let k = 0; k < total; k++) {
      const ecart = k - position;
      const d = Math.max(-1, Math.min(1, ecart));
      const p = 1 - Math.abs(d);
      const slide = this.slides[k];
      const cle = `${d.toFixed(3)}`;
      if (!force && slide.mkCle === cle) continue;
      slide.mkCle = cle;
      slide.style.setProperty('--d', d.toFixed(3));
      slide.style.setProperty('--p', p.toFixed(3));
      slide.classList.toggle('mk-cdc__slide--cote', p < 0.5);
      slide.classList.toggle('mk-cdc__slide--gauche', d < 0);
    }

    const nombre = this.originaux.length;
    const actif = ((Math.round(position) % nombre) + nombre) % nombre;
    if (actif !== this.pointActif) {
      this.pointActif = actif;
      this.points.forEach((point, i) => point.toggleAttribute('data-actif', i === actif));
    }
  }

  /* ---- Défilement automatique ---- */

  lancer() {
    this.arreter();
    if (mouvementReduit.matches) return;
    if (!this.visible || this.enPause || document.hidden) return;
    const intervalle = Number(this.dataset.intervalle) || 3000;
    this.minuterie = setInterval(() => this.avancer(1), intervalle);
  }

  arreter() {
    clearInterval(this.minuterie);
    this.minuterie = null;
  }

  /** Avance ou recule d'une slide, calée sur un cran pour ne jamais dériver. */
  avancer(sens) {
    if (!this.pas) return;
    if (this.anime) this.finir();
    this.animerVers((Math.round(this.scene.scrollLeft / this.pas) + sens) * this.pas);
  }

  /**
   * Glisse jusqu'à une position (0,9 s, courbe de la maquette). Animation faite
   * à la main : Safari annule un scrollBy smooth sur un conteneur à aimants.
   * Les aimants sont coupés le temps du mouvement puis rétablis.
   */
  animerVers(cible, duree = 900) {
    const scene = this.scene;
    const depart = scene.scrollLeft;
    if (mouvementReduit.matches) duree = 0;
    if (!duree) {
      scene.scrollLeft = cible;
      this.afficher();
      this.recentrer();
      return;
    }
    const debut = performance.now();
    this.anime = true;
    scene.style.scrollSnapType = 'none';

    const image = (instant) => {
      const avance = Math.min((instant - debut) / duree, 1);
      scene.scrollLeft = depart + (cible - depart) * courbe(avance);
      this.afficher();
      if (avance < 1) {
        this.image = requestAnimationFrame(image);
        return;
      }
      this.finir();
      this.recentrer();
    };
    this.image = requestAnimationFrame(image);
  }

  finir() {
    cancelAnimationFrame(this.image);
    this.scene.style.scrollSnapType = '';
    this.anime = false;
  }

  pause() {
    clearTimeout(this.minuterieReprise);
    this.enPause = true;
    this.arreter();
  }

  reprise() {
    clearTimeout(this.minuterieReprise);
    this.enPause = false;
    this.lancer();
  }

  /** Laisse retomber le glissement du doigt avant de relancer. */
  repriseDifferee() {
    clearTimeout(this.minuterieReprise);
    this.minuterieReprise = setTimeout(() => this.reprise(), 1000);
  }

  /* ---- Glisser à la souris ---- */

  /** Le défilement natif ne suit pas le glisser d'une souris : on le fait à la main. */
  brancherSouris() {
    const scene = this.scene;
    scene.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0 || !this.pas) return;
      this.pointeur = { id: event.pointerId, x: event.clientX, gauche: scene.scrollLeft };
      this.glisse = false;
    });
    scene.addEventListener('pointermove', (event) => {
      const pointeur = this.pointeur;
      if (!pointeur || event.pointerId !== pointeur.id) return;
      if (!this.glisse) {
        if (Math.abs(event.clientX - pointeur.x) < 5) return;
        if (this.anime) this.finir();
        this.pause();
        this.glisse = true;
        pointeur.x = event.clientX;
        pointeur.gauche = scene.scrollLeft;
        scene.style.scrollSnapType = 'none';
        this.toggleAttribute('data-glisse', true);
        try {
          scene.setPointerCapture(event.pointerId);
        } catch {
          /* Capture indisponible : le glisser marche tant que la souris reste dans la zone. */
        }
      }
      event.preventDefault();
      let gauche = pointeur.gauche - (event.clientX - pointeur.x);
      /* La boucle reste infinie pendant le glisser : on décale d'un jeu entier. */
      if (gauche < this.jeu * 0.5) {
        pointeur.gauche += this.jeu;
        gauche += this.jeu;
      } else if (gauche > this.jeu * 1.5) {
        pointeur.gauche -= this.jeu;
        gauche -= this.jeu;
      }
      scene.scrollLeft = gauche;
      this.afficher();
    });
    const relache = (event) => {
      const pointeur = this.pointeur;
      if (!pointeur || event.pointerId !== pointeur.id) return;
      this.pointeur = null;
      if (!this.glisse) return;
      this.glisse = false;
      this.toggleAttribute('data-glisse', false);
      try {
        scene.releasePointerCapture(event.pointerId);
      } catch {
        /* Déjà relâchée. */
      }
      /* Le clic suit immédiatement le relâchement : on l'ignore une fois. */
      this.clicBloque = true;
      setTimeout(() => (this.clicBloque = false), 0);

      /* Calage sur la slide la plus proche ; un glisser franc (15 % d'une slide)
         avance d'une slide dans son sens même s'il est court. */
      const gauche = scene.scrollLeft;
      let index = Math.round(gauche / this.pas);
      const delta = gauche - pointeur.gauche;
      if (Math.abs(delta) > this.pas * 0.15 && index === Math.round(pointeur.gauche / this.pas)) {
        index += Math.sign(delta);
      }
      this.animerVers(index * this.pas, 500);
      /* La souris a pu sortir pendant le glisser : pas de pointerleave dans ce cas. */
      if (!scene.matches(':hover')) this.reprise();
    };
    scene.addEventListener('pointerup', relache);
    scene.addEventListener('pointercancel', relache);
    scene.addEventListener('dragstart', (event) => event.preventDefault());

    /* Un glisser n'est pas un clic ; un clic sur une photo latérale l'amène au centre. */
    this.addEventListener(
      'click',
      (event) => {
        if (this.clicBloque) {
          event.preventDefault();
          event.stopPropagation();
          this.clicBloque = false;
          return;
        }
        if (!(event.target instanceof Element)) return;
        const photo = event.target.closest('.mk-cdc__photo');
        if (!photo) return;
        const index = this.slides.indexOf(photo.closest('.mk-cdc__slide'));
        if (index < 0 || index === Math.round(this.scene.scrollLeft / this.pas)) return;
        if (this.anime) this.finir();
        this.animerVers(index * this.pas);
        this.lancer();
      },
      true
    );
  }
}

/* -------------------------------------------------------------------------- */
/* Réassurance (carrousel mobile)                                             */
/* -------------------------------------------------------------------------- */

class MkReassurance extends HTMLElement {
  connectedCallback() {
    if (this.pret) return;
    this.piste = this.querySelector('.mk-reassurance__piste');
    this.points = Array.from(this.querySelectorAll('.mk-reassurance__point'));
    if (!this.piste) return;
    this.pret = true;

    let attente = false;
    this.piste.addEventListener(
      'scroll',
      () => {
        if (attente) return;
        attente = true;
        requestAnimationFrame(() => {
          attente = false;
          this.marquer();
        });
      },
      { passive: true }
    );

    const intervalle = Number(this.dataset.intervalle) || 3500;
    this.rotation = new Rotation(this, intervalle, () => this.suivant());
    this.piste.addEventListener('touchstart', () => this.rotation.pause('toucher'), { passive: true });
    const finToucher = () => {
      this.rotation.reprise('toucher');
      this.rotation.relancer();
    };
    this.piste.addEventListener('touchend', finToucher, { passive: true });
    this.piste.addEventListener('touchcancel', finToucher, { passive: true });
  }

  disconnectedCallback() {
    this.rotation?.arreter();
    this.pret = false;
  }

  get index() {
    const largeur = this.piste.clientWidth || 1;
    return Math.round(this.piste.scrollLeft / largeur);
  }

  marquer() {
    const index = this.index;
    this.points.forEach((point, i) => point.toggleAttribute('data-actif', i === index));
  }

  suivant() {
    if (ecranLarge.matches) return;
    const nombre = this.piste.children.length;
    const prochain = (this.index + 1) % nombre;
    this.piste.scrollTo({ left: prochain * this.piste.clientWidth, behavior: 'smooth' });
  }
}

/* -------------------------------------------------------------------------- */
/* Texte SEO : « Lire la suite » / « Réduire »                                 */
/* -------------------------------------------------------------------------- */

class MkSeoFaq extends HTMLElement {
  connectedCallback() {
    if (this.pret) return;
    this.pret = true;
    const bouton = this.querySelector('.mk-seo-faq__bascule');
    const libelle = this.querySelector('.mk-seo-faq__libelle');
    if (!bouton || !libelle) return;

    bouton.addEventListener('click', () => {
      const ouvert = this.toggleAttribute('data-ouvert');
      bouton.setAttribute('aria-expanded', String(ouvert));
      libelle.textContent = ouvert ? bouton.dataset.fermer : bouton.dataset.ouvrir;
      if (!ouvert) {
        const haut = this.getBoundingClientRect().top;
        if (haut < 0) this.scrollIntoView({ block: 'start', behavior: mouvementReduit.matches ? 'auto' : 'smooth' });
      }
    });
  }
}

/* -------------------------------------------------------------------------- */
/* Vidéo : chargée à l'approche de l'écran, en pause dès qu'elle en sort       */
/* -------------------------------------------------------------------------- */

class MkVideoAuto extends HTMLElement {
  connectedCallback() {
    if (this.pret) return;
    this.video = this.querySelector('video');
    if (!this.video) return;
    this.pret = true;

    const economie = navigator.connection && navigator.connection.saveData;
    if (mouvementReduit.matches || economie) return;

    this.video.addEventListener('playing', () => this.toggleAttribute('data-lecture', true), { once: true });

    this.observateur = new IntersectionObserver(
      (entrees) => {
        if (entrees[entrees.length - 1].isIntersecting) {
          if (!this.video.src && this.video.dataset.src) this.video.src = this.video.dataset.src;
          this.video.play().catch(() => {});
        } else if (!this.video.paused) {
          this.video.pause();
        }
      },
      { rootMargin: '300px 0px' }
    );
    /* L'élément lui-même est en display: contents (pas de boîte) : on observe la vidéo. */
    this.observateur.observe(this.video);
  }

  disconnectedCallback() {
    this.observateur?.disconnect();
    this.pret = false;
  }
}

for (const [nom, classe] of [
  ['mk-coups-de-coeur', MkCoupsDeCoeur],
  ['mk-reassurance', MkReassurance],
  ['mk-seo-faq', MkSeoFaq],
  ['mk-video-auto', MkVideoAuto],
]) {
  if (!customElements.get(nom)) customElements.define(nom, classe);
}
